import { apiSlice } from './apiSlice';
import { budgetApi as externalBudgetApi } from '@/services/accountingService';

/**
 * These endpoints call the accounting service directly, so they bypass the
 * shared axios base query and unwrap the backend's `{ success, message, data }`
 * envelope themselves. Both halves live here so the eleven endpoints cannot
 * drift apart: the failure keeps the structured backend body (including the
 * field-level `errors` map) instead of being flattened to a bare message, and a
 * network failure still resolves to a concrete status — `error.response?.status`
 * alone was `undefined` for those, which RTK Query cannot represent.
 */
const budgetError = (error: any) => ({
  status: error?.response?.status ?? error?.status ?? 'FETCH_ERROR',
  data: error?.response?.data ?? error?.message ?? null,
});

const budgetData = (response: any) => response?.data;

export const budgetApi = apiSlice.injectEndpoints({
   // Replace with actual base query if needed
  endpoints: (builder) => ({
    getBudgets: builder.query<any, Record<string, any> | void>({
      queryFn: async (params = {}) => {
        try {
          // `params` is `void` for a bare `useGetBudgetsQuery()`; the service's
          // own default already covers undefined, so no second `|| {}` here.
          const response: any = await externalBudgetApi.getAll(params as Record<string, any> | undefined);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      providesTags: ['Budget'],
    }),
    getBudgetById: builder.query<any, number | string>({
      queryFn: async (id) => {
        try {
          const response: any = await externalBudgetApi.getById(id);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      providesTags: (result, error, id) => [{ type: 'Budget', id }],
    }),
    createBudget: builder.mutation<any, any>({
      queryFn: async (data) => {
        try {
          const response: any = await externalBudgetApi.create(data);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      invalidatesTags: ['Budget'],
    }),
    updateBudget: builder.mutation<any, { id: number | string; data: any }>({
      queryFn: async ({ id, data }) => {
        try {
          const response: any = await externalBudgetApi.update(id, data);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Budget', id }, 'Budget'],
    }),
    deleteBudget: builder.mutation<void, number | string>({
      queryFn: async (id) => {
        try {
          await externalBudgetApi.delete(id);
          return { data: undefined };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      invalidatesTags: ['Budget'],
    }),
    approveBudget: builder.mutation<any, number | string>({
      queryFn: async (id) => {
        try {
          const response: any = await externalBudgetApi.approve(id);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      invalidatesTags: (result, error, id) => [{ type: 'Budget', id }, 'Budget'],
    }),
    getVariance: builder.query<any, { id: number | string; params?: Record<string, any> }>({
      queryFn: async ({ id, params = {} }) => {
        try {
          const response: any = await externalBudgetApi.getVariance(id, params);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      providesTags: (result, error, { id }) => [{ type: 'Variance', id }],
    }),
    computeVariance: builder.mutation<any, number | string>({
      queryFn: async (id) => {
        try {
          const response: any = await externalBudgetApi.computeVariance(id);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      invalidatesTags: (result, error, id) => [{ type: 'Variance', id }],
    }),
    createEncumbrance: builder.mutation<any, { id: number | string; data: any }>({
      queryFn: async ({ id, data }) => {
        try {
          const response: any = await externalBudgetApi.createEncumbrance(id, data);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      // An encumbrance changes the budget's variance figures, and `getVariance`
      // is cached under `{ type: 'Variance', id }` — without that tag the
      // variance panel kept rendering pre-encumbrance numbers.
      invalidatesTags: (result, error, { id }) => [{ type: 'Budget', id }, { type: 'Variance', id }],
    }),
    liquidateEncumbrance: builder.mutation<any, { budgetId: number | string; encumbranceId: number | string }>({
      queryFn: async ({ budgetId, encumbranceId }) => {
        try {
          const response: any = await externalBudgetApi.liquidateEncumbrance(budgetId, encumbranceId);
          return { data: budgetData(response) };
        } catch (error: any) {
          return { error: budgetError(error) };
        }
      },
      // Liquidating also changes the budget's variance figures (same as
      // createEncumbrance), so refresh the Variance tag as well.
      invalidatesTags: (result, error, { budgetId }) => [{ type: 'Budget', id: budgetId }, { type: 'Variance', id: budgetId }],
    }),
  }),
});

export const {
  useGetBudgetsQuery,
  useLazyGetBudgetsQuery,
  useGetBudgetByIdQuery,
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
  useDeleteBudgetMutation,
  useApproveBudgetMutation,
  useGetVarianceQuery,
  useComputeVarianceMutation,
  useCreateEncumbranceMutation,
  useLiquidateEncumbranceMutation,
} = budgetApi;

export default budgetApi;
